"use strict";
// Shop — catalog, SKUs, inventory, orders. Sells through Payments.
//
// Shop doesn't know which provider takes the money: it requires
// `payments.checkout@1` and registers the `shop.order` purpose. Shop itself is
// made of modules (catalog, variants, inventory, orders, discounts, shipping,
// tax…) that an owner switches on per shop; shipping, tax and notification
// backends are its own extension points so carriers (Shiprocket), tax engines
// and WhatsApp updates are separate provider plugins.
Object.defineProperty(exports, "__esModule", { value: true });
exports.SHOP_EMAILS_PLUGIN = exports.INCLUSIVE_TAX_PLUGIN = exports.FLAT_SHIPPING_PLUGIN = exports.SHOP_PLUGIN = exports.DEFAULT_SHOP_MODULES = exports.SHOP_MODULES = void 0;
exports.SHOP_MODULES = [
    'catalog',
    'variants',
    'inventory',
    'pricing',
    'orders',
    'customers',
    'discounts',
    'shipping',
    'tax',
    'notifications',
    'reviews',
    'digital',
    'abandoned-carts',
    'analytics',
];
/** Modules on by default for a new shop. */
exports.DEFAULT_SHOP_MODULES = [
    'catalog',
    'variants',
    'inventory',
    'pricing',
    'orders',
    'shipping',
    'tax',
    'notifications',
];
exports.SHOP_PLUGIN = {
    id: 'shop',
    version: '1.0.0',
    kind: 'capability',
    trust: 'first-party',
    title: 'Shop',
    description: 'Products, variants and SKUs, inventory, orders and fulfilment. Payments via any provider.',
    icon: 'i-ph:storefront',
    surfaces: ['web-app'],
    agentCard: 'Shop: products/variants/SKUs, inventory ledger, cart, orders (skills/shop.md). Needs Payments.',
    requires: {
        'payments.checkout': { major: 1 },
        'auth.session': { major: 1, optional: true },
    },
    provides: {
        'payments.purpose': { major: 1, features: { oneTime: true, quantity: true }, meta: { purpose: 'shop.order' } },
        // Warns before publish when the live site can't take real payments.
        'core.deploy.check': { major: 1 },
    },
    declares: {
        'shop.shippingRate': {
            major: 1,
            cardinality: 'many',
            description: 'Quote shipping for a cart (flat, zones, live carrier rates).',
            features: ['flat', 'zones', 'liveRates', 'labels', 'tracking'],
        },
        'shop.taxCalculator': {
            major: 1,
            cardinality: 'one',
            description: 'Compute tax for a cart (inclusive pricing, GST per HSN, Stripe Tax…).',
            features: ['inclusive', 'exclusive', 'gst', 'automatic'],
        },
        'shop.notifier': {
            major: 1,
            cardinality: 'many',
            description: 'Send order updates (email, WhatsApp, SMS).',
            features: ['email', 'whatsapp', 'sms'],
        },
    },
    emits: {
        'shop.order.paid': { major: 1, description: 'An order was paid.' },
        'shop.order.fulfilled': { major: 1, description: 'An order was marked shipped/fulfilled.' },
        'shop.order.refunded': { major: 1, description: 'An order was refunded (fully or partially).' },
        'shop.stock.low': { major: 1, description: 'A SKU fell below its low-stock threshold.' },
    },
    subscribes: [
        'payments.payment.succeeded',
        'payments.payment.refunded',
        'payments.payment.disputed',
        'payments.payment.reference_submitted',
    ],
    permissions: ['db:own-tables', 'storage:images', 'email:send'],
    settings: [
        {
            id: 'store',
            title: 'Store',
            fields: [
                { key: 'name', label: 'Store name', type: 'text', required: true },
                { key: 'currency', label: 'Currency', type: 'text', placeholder: 'INR', required: true },
                { key: 'skuPattern', label: 'SKU pattern', type: 'text', placeholder: '{PRODUCT}-{OPTION1}-{OPTION2}' },
                { key: 'taxInclusive', label: 'Prices include tax', type: 'boolean' },
                { key: 'lowStockThreshold', label: 'Low-stock alert at', type: 'number', placeholder: '5' },
            ],
        },
    ],
    targets: {
        'web-app': {
            skills: ['shop'],
            lockedPaths: ['frontend/worker/dunea/shop.ts', 'frontend/src/dunea/shop.tsx'],
            detect: ['useCart'],
            tablePrefix: 'p_shop_',
            budgetGzipKb: 40,
        },
    },
};
exports.FLAT_SHIPPING_PLUGIN = {
    id: 'flat-shipping',
    version: '1.0.0',
    kind: 'provider',
    trust: 'first-party',
    title: 'Flat-rate shipping',
    description: 'One shipping price, optionally free over an order amount.',
    provides: { 'shop.shippingRate': { major: 1, features: { flat: true } } },
};
exports.INCLUSIVE_TAX_PLUGIN = {
    id: 'inclusive-tax',
    version: '1.0.0',
    kind: 'provider',
    trust: 'first-party',
    title: 'Tax-inclusive pricing',
    description: 'Prices already include tax (common for GST and VAT). Shows the tax portion on invoices.',
    provides: { 'shop.taxCalculator': { major: 1, features: { inclusive: true, gst: true } } },
};
/**
 * Transactional order emails (host-only, on by default). Studio subscribes on
 * behalf of this plugin (apps/studio/src/modules/plugins/shop/shop-notifier.ts),
 * so an owner who disables it stops the emails without touching the shop.
 */
exports.SHOP_EMAILS_PLUGIN = {
    id: 'shop-emails',
    version: '1.0.0',
    kind: 'provider',
    trust: 'first-party',
    title: 'Order emails',
    description: 'Emails the buyer an order confirmation, shipping update and refund notice, and emails you about every new order.',
    icon: 'i-ph:envelope-simple',
    provides: { 'shop.notifier': { major: 1, features: { email: true } } },
    subscribes: ['shop.order.paid', 'shop.order.fulfilled', 'shop.order.refunded'],
    permissions: ['email:send'],
};
